import React, { lazy } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ContactForm } from '../components/ContactForm';
import { PhoneIcon, MapPinIcon, MailIcon, FacebookIcon, InstagramIcon, StarIcon, ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
const headingStyle = {
  color: '#0283C7',
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 900 as const,
  lineHeight: 1.2
};
const bodyStyle = {
  color: '#025A8A',
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  lineHeight: 1.8
};
const subHeadingStyle = {
  color: '#0283C7',
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 800 as const,
  lineHeight: 1.3
};
const btnStyle = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 700 as const
};
export function ContactPage() {
  return <div className="w-full min-h-screen" style={{
    fontFamily: 'Inter, sans-serif'
  }} data-id="element-902">
      <Navbar data-id="element-903" />
      <main data-id="element-904">
        {/* ===== Section 1 — Hero ===== */}
        <section className="w-full py-20 md:py-28 px-4" style={{
        backgroundColor: '#0283C7'
      }} data-id="element-905">
          <div className="max-w-4xl mx-auto text-center" data-id="element-906">
            <p className="uppercase tracking-[0.2em] mb-4 text-sm" style={{
            color: 'rgba(255,255,255,0.85)',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700
          }} data-id="element-907">
              Contact Us
            </p>
            <h1 className="text-white font-bold mb-6" style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(28px, 5vw, 48px)',
            lineHeight: 1.15
          }} data-id="element-908">
              Get In Touch
            </h1>
            <p className="mb-10 mx-auto max-w-2xl" style={{
            color: 'rgba(255,255,255,0.9)',
            fontFamily: 'Inter, sans-serif',
            fontSize: '18px',
            lineHeight: 1.7
          }} data-id="element-909">
              Send us a message using the form and we'll get back to you as soon
              as possible.
            </p>
            <a href="#contact-form" className="inline-block w-full sm:w-auto text-center px-10 py-4 rounded-lg text-base transition-all hover:opacity-90 hover:shadow-lg" style={{
            backgroundColor: '#ffffff',
            color: '#0283C7',
            ...btnStyle
          }} data-id="element-910">
              Get a Free Quote
            </a>
          </div>
        </section>

        {/* ===== Section 2 — Contact Info + Form ===== */}
        <section id="contact-form" className="w-full py-20 px-4" style={{
        backgroundColor: '#ffffff'
      }} data-id="element-911">
          <div className="max-w-7xl mx-auto" data-id="element-912">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16" data-id="element-913">
              {/* Left — Contact Information */}
              <div className="w-full lg:w-[40%]" data-id="element-914">
                <h2 className="font-bold mb-8" style={{
                ...headingStyle,
                fontSize: '24px'
              }} data-id="element-915">
                  Contact Information
                </h2>

                <div className="flex flex-col gap-6" data-id="element-916">
                  {/* Maricopa Address */}
                  <a href="https://maps.google.com/?q=45004+W+Garvey+Ave+Maricopa+AZ+85139" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 transition-opacity hover:opacity-80" data-id="element-917">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: '#E8F4FD'
                  }} data-id="element-918">
                      <MapPinIcon className="h-5 w-5" style={{
                      color: '#0283C7'
                    }} data-id="element-919" />
                    </div>
                    <div data-id="element-920">
                      <p className="font-semibold text-sm mb-0.5" style={{
                      color: '#0283C7',
                      fontFamily: 'Montserrat, sans-serif'
                    }} data-id="element-921">
                        Maricopa Office
                      </p>
                      <p style={{
                      ...bodyStyle,
                      fontSize: '14px'
                    }} data-id="element-922">
                        45004 W. Garvey Ave.
                        <br data-id="element-923" />
                        Maricopa, AZ 85139
                      </p>
                    </div>
                  </a>

                  {/* Mesa Address */}
                  <a href="https://maps.google.com/?q=663+W+2nd+Ave+Suite+17+Mesa+AZ+85210" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 transition-opacity hover:opacity-80" data-id="element-924">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: '#E8F4FD'
                  }} data-id="element-925">
                      <MapPinIcon className="h-5 w-5" style={{
                      color: '#0283C7'
                    }} data-id="element-926" />
                    </div>
                    <div data-id="element-927">
                      <p className="font-semibold text-sm mb-0.5" style={{
                      color: '#0283C7',
                      fontFamily: 'Montserrat, sans-serif'
                    }} data-id="element-928">
                        Mesa Office
                      </p>
                      <p style={{
                      ...bodyStyle,
                      fontSize: '14px'
                    }} data-id="element-929">
                        663 W 2nd Ave Suite 17
                        <br data-id="element-930" />
                        Mesa, AZ 85210
                      </p>
                    </div>
                  </a>

                  {/* Phone */}
                  <a href="tel:4805998442" className="flex items-center gap-4 transition-opacity hover:opacity-80" data-id="element-931">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: '#E8F4FD'
                  }} data-id="element-932">
                      <PhoneIcon className="h-5 w-5" style={{
                      color: '#0283C7'
                    }} data-id="element-933" />
                    </div>
                    <div data-id="element-934">
                      <p className="font-semibold text-sm mb-0.5" style={{
                      color: '#0283C7',
                      fontFamily: 'Montserrat, sans-serif'
                    }} data-id="element-935">
                        Phone
                      </p>
                      <p style={{
                      ...bodyStyle,
                      fontSize: '14px'
                    }} data-id="element-936">
                        +1 (480) 599-8442
                      </p>
                    </div>
                  </a>

                  {/* Email */}
                  <a href="mailto:info@bluecactuspowerwashing.com" className="flex items-center gap-4 transition-opacity hover:opacity-80" data-id="element-937">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: '#E8F4FD'
                  }} data-id="element-938">
                      <MailIcon className="h-5 w-5" style={{
                      color: '#0283C7'
                    }} data-id="element-939" />
                    </div>
                    <div data-id="element-940">
                      <p className="font-semibold text-sm mb-0.5" style={{
                      color: '#0283C7',
                      fontFamily: 'Montserrat, sans-serif'
                    }} data-id="element-941">
                        Email
                      </p>
                      <p style={{
                      ...bodyStyle,
                      fontSize: '14px'
                    }} data-id="element-942">
                        info@bluecactuspowerwashing.com
                      </p>
                    </div>
                  </a>
                </div>

                {/* Social Icons */}
                <div className="mt-10" data-id="element-943">
                  <p className="font-bold text-sm mb-4 uppercase tracking-widest" style={{
                  color: '#0283C7',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px'
                }} data-id="element-944">
                    Follow Us
                  </p>
                  <div className="flex items-center gap-3" data-id="element-945">
                    {[{
                    icon: <FacebookIcon className="h-5 w-5" data-id="element-946" />,
                    label: 'Facebook',
                    href: 'https://www.facebook.com/people/Blue-Cactus-Power-Washing/100087759273733/#'
                  }, {
                    icon: <InstagramIcon className="h-5 w-5" data-id="element-947" />,
                    label: 'Instagram',
                    href: 'https://www.instagram.com/bluecactuspowerwashing'
                  }, {
                    icon: <StarIcon className="h-5 w-5" data-id="element-948" />,
                    label: 'Google Reviews',
                    href: 'https://share.google/uDUGzbAlF3nHOVPWX'
                  }].map(({
                    icon,
                    label,
                    href
                  }) => <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full flex items-center justify-center transition-all hover:scale-110" style={{
                    backgroundColor: '#E8F4FD',
                    color: '#0283C7'
                  }} aria-label={label} data-id="element-949">
                        {icon}
                      </a>)}
                  </div>
                </div>
              </div>

              {/* Right — Contact Form */}
              <div className="w-full lg:w-[60%]" data-id="element-950">
                <div className="p-8 md:p-10 rounded-2xl" style={{
                backgroundColor: '#F8FBFE',
                boxShadow: '0 4px 24px rgba(2,131,199,0.08)'
              }} data-id="element-951">
                  <ContactForm data-id="element-952" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== Section 3 — Map + Location Cards ===== */}
        <section className="w-full py-20 px-4" style={{
        backgroundColor: '#E8F4FD'
      }} data-id="element-953">
          <div className="max-w-7xl mx-auto" data-id="element-954">
            {/* Map Placeholder */}
            <div className="w-full rounded-2xl overflow-hidden mb-10" style={{
            height: '400px',
            backgroundColor: '#c8e3f5',
            boxShadow: '0 4px 20px rgba(2,131,199,0.1)'
          }} data-id="element-955">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d426144.2!2d-112.1!3d33.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x872b0892a4a1c96d%3A0x3e4e6f6b6e6b6e6b!2sMaricopa%2C%20AZ!5e0!3m2!1sen!2sus!4v1700000000000" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Blue Cactus Power Washing Locations" data-id="element-956" />
            </div>

            {/* Location Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-957">
              <div className="bg-white rounded-2xl p-8 flex items-start gap-5" style={{
              boxShadow: '0 4px 20px rgba(2,131,199,0.08)'
            }} data-id="element-958">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#0283C7'
              }} data-id="element-959">
                  <MapPinIcon className="h-6 w-6 text-white" data-id="element-960" />
                </div>
                <div data-id="element-961">
                  <h3 className="font-bold mb-2" style={{
                  ...subHeadingStyle,
                  fontSize: '18px'
                }} data-id="element-962">
                    Maricopa Office
                  </h3>
                  <p className="mb-3" style={{
                  ...bodyStyle,
                  fontSize: '14px'
                }} data-id="element-963">
                    45004 W. Garvey Ave.
                    <br data-id="element-964" />
                    Maricopa, AZ 85139
                  </p>
                  <a href="https://maps.google.com/?q=45004+W+Garvey+Ave+Maricopa+AZ+85139" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold" style={{
                  color: '#0283C7',
                  fontFamily: 'Inter, sans-serif'
                }} data-id="element-965">
                    Get Directions <ArrowRightIcon className="h-4 w-4" data-id="element-966" />
                  </a>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 flex items-start gap-5" style={{
              boxShadow: '0 4px 20px rgba(2,131,199,0.08)'
            }} data-id="element-967">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#0283C7'
              }} data-id="element-968">
                  <MapPinIcon className="h-6 w-6 text-white" data-id="element-969" />
                </div>
                <div data-id="element-970">
                  <h3 className="font-bold mb-2" style={{
                  ...subHeadingStyle,
                  fontSize: '18px'
                }} data-id="element-971">
                    Mesa Office
                  </h3>
                  <p className="mb-3" style={{
                  ...bodyStyle,
                  fontSize: '14px'
                }} data-id="element-972">
                    663 W 2nd Ave Suite 17
                    <br data-id="element-973" />
                    Mesa, AZ 85210
                  </p>
                  <a href="https://maps.google.com/?q=663+W+2nd+Ave+Suite+17+Mesa+AZ+85210" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold" style={{
                  color: '#0283C7',
                  fontFamily: 'Inter, sans-serif'
                }} data-id="element-974">
                    Get Directions <ArrowRightIcon className="h-4 w-4" data-id="element-975" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== Section 4 — Bottom CTA Banner ===== */}
        <section className="w-full py-16 px-4" style={{
        backgroundColor: '#0283C7'
      }} data-id="element-976">
          <div className="max-w-4xl mx-auto text-center" data-id="element-977">
            <h2 className="text-white font-bold mb-4" style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(24px, 4vw, 36px)',
            lineHeight: 1.2
          }} data-id="element-978">
              Prefer to Call?
            </h2>
            <a href="tel:4805998442" className="inline-block text-white font-bold mb-6 transition-opacity hover:opacity-80" style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(32px, 5vw, 48px)',
            lineHeight: 1.2
          }} data-id="element-979">
              (480) 599-8442
            </a>
            <div className="flex items-center justify-center gap-2" style={{
            color: 'rgba(255,255,255,0.8)',
            fontFamily: 'Inter, sans-serif',
            fontSize: '16px'
          }} data-id="element-980">
              <ShieldCheckIcon className="h-4 w-4" data-id="element-981" />
              <span data-id="element-982">Licensed • Bonded • Insured</span>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-983" />
    </div>;
}